import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { AnalyticsView } from '@/components/stream/AnalyticsView'
import { accountQuery, subaccountsQuery } from '@/lib/queries'
import { streamConfigQuery, streamHitsQuery } from '@/lib/stream-queries'
import { primarySubaccount } from '@/lib/stream-workspace'

/**
 * The account workspace's Analytics — the aggregate view of the same stream Library lists. Gated
 * on `delivery:analytics`; KPI drill-throughs navigate to this workspace's Library with the
 * narrowing in the URL.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/analytics')({
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.analytics)) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    const subs = await context.queryClient.ensureQueryData(subaccountsQuery(params.accountId))
    const primary = primarySubaccount(subs.subaccounts)
    // Started, not awaited — see the account's Library route (CLI-758): the canvas paints at
    // once and its modules fill in when the rows land.
    void context.queryClient.prefetchQuery(streamHitsQuery(primary.id))
    await context.queryClient.ensureQueryData(streamConfigQuery(primary.id))
  },
  component: AccountAnalyticsPage,
})

function AccountAnalyticsPage() {
  const { accountId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()
  const { data: subs } = useSuspenseQuery(subaccountsQuery(accountId))

  return (
    <AnalyticsView
      storageKey={accountId}
      subaccountId={primarySubaccount(subs.subaccounts).id}
      accountId={accountId}
      onDrill={(drill) =>
        void navigate({
          to: '/app/accounts/$accountId/library',
          params: { accountId },
          search: drill,
        })
      }
      libraryHref={(drill) =>
        router.buildLocation({
          to: '/app/accounts/$accountId/library',
          params: { accountId },
          search: drill,
        }).href
      }
      onConfigure={() =>
        void navigate({ to: '/app/accounts/$accountId/stream-config', params: { accountId } })
      }
    />
  )
}
